'use client';

import React from 'react';
import {
  ShieldCheck,
  Zap,
  Clock,
  TrendingUp,
  Lock,
  Headphones,
  CheckCircle2,
  Sparkles,
  Award,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export function StatsAndProofSection() {
  const stats = [
    {
      value: '3 in 1',
      label: 'Omnichannel Console',
      subtext: 'WhatsApp, Instagram & Facebook unified in real-time',
      icon: Zap,
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      value: '< 800ms',
      label: 'Average AI Latency',
      subtext: 'Instant conversational replies with vector grounding',
      icon: Clock,
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      value: '85%',
      label: 'Autonomous Resolution',
      subtext: 'Inquiries, bookings & catalogs closed without human labor',
      icon: TrendingUp,
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      value: '99.99%',
      label: 'Cloud Delivery SLA',
      subtext: 'Resilient Meta Graph API v21.0 & Pusher engine',
      icon: ShieldCheck,
      gradient: 'from-amber-500 to-orange-600',
    },
  ];

  const trustBadges = [
    {
      icon: ShieldCheck,
      title: 'Official Meta Graph API Ready',
      desc: 'Native Messenger, Instagram & WhatsApp Business Cloud API architecture.',
    },
    {
      icon: Lock,
      title: 'Enterprise Encryption & Privacy',
      desc: 'Encrypted message tokens, customer data isolation, and strict role permissions.',
    },
    {
      icon: Headphones,
      title: '24/7 Human Agent Co-Pilot',
      desc: 'Seamless manual takeover anytime with collision detection and presence tracking.',
    },
    {
      icon: Award,
      title: 'Anti-Ban Delivery Pacing',
      desc: 'Smart rate-limiting and campaign delay queues for high-volume broadcasts.',
    },
  ];

  return (
    <div className="relative mx-auto max-w-6xl w-full text-left">
      {/* 4 Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;

          return (
            <div
              key={idx}
              className="p-6 rounded-3xl border border-border/70 bg-card/80 backdrop-blur-xl shadow-lg hover:border-primary/40 hover:shadow-xl transition-all group relative overflow-hidden"
            >
              <div className="size-10 rounded-2xl bg-muted/60 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Icon className="size-5 text-foreground" />
              </div>

              <div className="space-y-1">
                <div className={`text-3xl sm:text-4xl font-extrabold tracking-tight bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent font-mono`}>
                  {stat.value}
                </div>
                <h4 className="text-sm font-bold text-foreground">
                  {stat.label}
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {stat.subtext}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Trust & Security Banner */}
      <div className="rounded-3xl border border-border/80 bg-muted/20 p-8 lg:p-10 backdrop-blur-md">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8 pb-6 border-b border-border/60">
          <div>
            <Badge className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold mb-2">
              Enterprise Grade Reliability
            </Badge>
            <h3 className="text-xl md:text-2xl font-bold text-foreground">
              Built for High-Growth Businesses That Cannot Afford Downtime
            </h3>
          </div>
          <span className="text-xs font-mono font-bold text-emerald-600 bg-emerald-500/10 px-3 py-1.5 rounded-full shrink-0">
            ● All Systems Operational
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustBadges.map((badge, idx) => {
            const Icon = badge.icon;

            return (
              <div key={idx} className="space-y-2">
                <div className="flex items-center gap-2 font-bold text-sm text-foreground">
                  <Icon className="size-4 text-primary shrink-0" />
                  <span>{badge.title}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {badge.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
